import QuestionCircleTwoTone from '@ant-design/icons/lib/icons/QuestionCircleTwoTone';
import { App, Button, Divider, Input, InputNumber, Select, Table, Tag, Tooltip } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useDepartments } from '@/common/useDepartments';
import { useTableSkeleton } from '@/components/skeletons/useTableSkeleton';
import { useAuth } from '@/core/providers/AuthFirebaseProvider';
import { Roles } from '@/core/utils/roles';
import type { GetDistrictsDriverListQuery } from '@/generated/graphql/graphql';
import { formatError, logError } from '@/utils/formatError';
import { useUpdateDeliveryAreasFromGeo } from '../query/coverage.hooks';
import {
  useChangeDistrictProviders,
  useGetAvailableProviders,
  useGetDistrictsDriverList,
  useUpdateCutoffHours,
  useUpdateDistrictDeliveryDays,
  useUpdateDistrictLoadFactor,
  useUpdateDistrictOffDays,
} from '../query/districtsTable.hooks';
import type { SecondTableRowStates } from '../types';
import { DepartmentExpressToggle } from './DepartmentExpressToggle';
import { DriverWithDistrictsViewNew } from './DriverWithDistrictsViewNew';
import { ScheduleModal } from './ScheduleModal';

export const DistrictsTableViewNew = () => {
  const { message } = App.useApp();
  const [secondTableRowStates, setSecondTableRowStates] = useState<SecondTableRowStates>({});
  const [tableData, setTableData] = useState<Record<string, unknown>[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const { selectedDepartmentId: departmentId, selectedDepartment } = useDepartments();
  const { userDataParams } = useAuth();
  const canUpdateCoverage = Boolean(
    userDataParams?.roles.includes(Roles.manager) ||
      userDataParams?.roles.includes(Roles.seniorOperator)
  );
  const { mutateAsync: updateCoverage, isPending: isUpdatingCoverage } =
    useUpdateDeliveryAreasFromGeo();
  const [editingProviders, setEditingProviders] = useState<Record<string, string[]>>({});
  const [savingDistricts, setSavingDistricts] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleDistrictsData = useCallback((data: GetDistrictsDriverListQuery) => {
    const newSecondTableRowStates: SecondTableRowStates = {};

    data.district.forEach((district) => {
      const driverMap = new Map();

      district.weekday_window_districts.forEach((weekdayWindowDistrict) => {
        const driverWindow = weekdayWindowDistrict?.weekday_window?.driver_window;

        if (driverWindow && !driverWindow.user?.blocked) {
          const driverId = driverWindow.driverId;
          const driverName = driverWindow.user?.name;

          if (!driverMap.has(driverId)) {
            driverMap.set(driverId, { driverName, driverId });
          }
        }
      });

      const uniqueDrivers = Array.from(driverMap.values());

      newSecondTableRowStates[district.id] = {
        drivers: uniqueDrivers,
        enabled: district.enabled ?? false,
      };
    });

    setSecondTableRowStates(newSecondTableRowStates);

    setTableData(
      data.district.map((district, index: number) => ({
        key: index,
        districts: district.name,
        data: district.id,
        providers: {
          providers: (district.district_delivery_providers || []).map(
            (ddp: { delivery_provider_id: string }) => ddp.delivery_provider_id
          ),
          districtId: district.id,
        },
        loadFactor: {
          loadFactor: district.loadFactor,
          districtId: district.id,
        },
        deliveryDays: {
          deliveryDays: district.deliveryDays,
          districtId: district.id,
        },
        offDays: {
          offDays: district.offDays,
          districtId: district.id,
        },
        enabled: {
          enabled: district.enabled,
          districtId: district.id,
        },
        cutoffHours: district.cutoffHours,
        isPolygone: district.isPolygone,
      }))
    );
  }, []);

  const { data: districts, isLoading: districtsLoading } = useGetDistrictsDriverList({
    departmentId,
    searchTerm: debouncedSearchTerm ? `%${debouncedSearchTerm}%` : undefined,
  });
  const { data: providersData } = useGetAvailableProviders({
    countryId: String(selectedDepartment?.countryId ?? 0),
  });

  useEffect(() => {
    if (districts) {
      handleDistrictsData(districts);
    }
  }, [districts, handleDistrictsData]);

  const { mutateAsync: updateDistrictLoadFactor } = useUpdateDistrictLoadFactor();
  const { mutateAsync: updateDistrictOffDays } = useUpdateDistrictOffDays();
  const { mutateAsync: updateDistrictDeliveryDays } = useUpdateDistrictDeliveryDays();
  const { mutateAsync: updateCutoffHours } = useUpdateCutoffHours();
  const { mutateAsync: changeDistrictProviders } = useChangeDistrictProviders();

  const handleCutoffHoursChange = async (hours: number, districtId: string) => {
    try {
      await updateCutoffHours({
        districtId,
        hours,
      });
    } catch (error) {
      const errorMessage = formatError(error, 'Update Cutoff Hours');
      logError(error, 'Update Cutoff Hours');
      message.error(`Error updating cutoff hours: ${errorMessage}`);
    }
  };

  const handleLoadFactorChange = async (loadFactor: number, districtId: string) => {
    try {
      await updateDistrictLoadFactor({ loadFactor, districtId });
    } catch (error) {
      const errorMessage = formatError(error, 'Update Load Factor');
      logError(error, 'Update Load Factor');
      message.error(`Error updating load factor: ${errorMessage}`);
    }
  };

  const handleDeliveryDaysChange = async (deliveryDays: number, districtId: string) => {
    try {
      await updateDistrictDeliveryDays({ deliveryDays, districtId });
    } catch (error) {
      const errorMessage = formatError(error, 'Update Delivery Days');
      logError(error, 'Update Delivery Days');
      message.error(`Error updating delivery days: ${errorMessage}`);
    }
  };

  const handleOffDaysChange = async (offDays: number[], districtId: string) => {
    try {
      await updateDistrictOffDays({ offDays, districtId });
    } catch (error) {
      const errorMessage = formatError(error, 'Update Off Days');
      logError(error, 'Update Off Days');
      message.error(`Error updating off days: ${errorMessage}`);
    }
  };

  const columns = [
    {
      title: 'Districts',
      dataIndex: 'districts',
      render: (text: unknown, record: Record<string, unknown>) => {
        return (
          <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
            {String(text)}{' '}
            {Boolean(record.isPolygone) && (
              <ScheduleModal
                districtId={record.data as string}
                districtName={record.districts as string}
              />
            )}
          </div>
        );
      },
    },
    {
      title: 'Providers',
      dataIndex: 'providers',
      render: (providersProps: { providers: string[]; districtId: string }) => {
        const allProviders = providersData?.delivery_provider ?? [];
        const options = allProviders.map((p) => ({ label: `${p.display_name}`, value: p.id }));

        const current = editingProviders[providersProps.districtId] ?? providersProps.providers;

        const hasChanges = (() => {
          const a = Array.from(new Set(current)).sort();
          const b = Array.from(new Set(providersProps.providers)).sort();
          if (a.length !== b.length) return true;
          for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) return true;
          }
          return false;
        })();

        const onChange = (vals: string[]) => {
          const unique = Array.from(new Set(vals));
          setEditingProviders((prev) => ({ ...prev, [providersProps.districtId]: unique }));
        };

        const onSave = async () => {
          const districtId = providersProps.districtId;
          const finalList = Array.from(new Set(current));
          setSavingDistricts((prev) => ({ ...prev, [districtId]: true }));
          try {
            await changeDistrictProviders({ districtId, providers: finalList });
            setTableData((prev) =>
              prev.map((row) =>
                row.data === districtId
                  ? {
                      ...row,
                      providers: {
                        ...(row.providers as Record<string, unknown>),
                        providers: finalList,
                      },
                    }
                  : row
              )
            );
            setEditingProviders((prev) => {
              const copy = { ...prev };
              delete copy[districtId];
              return copy;
            });
            message.success('Providers updated');
          } catch (_e) {
            message.error('Failed to update providers');
          } finally {
            setSavingDistricts((prev) => ({ ...prev, [districtId]: false }));
          }
        };

        const onCancel = () => {
          setEditingProviders((prev) => {
            const copy = { ...prev };
            delete copy[providersProps.districtId];
            return copy;
          });
        };

        return (
          <div>
            <Select
              mode="multiple"
              allowClear
              style={{ width: '100%' }}
              placeholder="Select providers"
              value={current}
              options={options.map((opt) => {
                const provider = allProviders.find((p) => p.id === opt.value);
                const isExpress = provider?.short_name === 'DTK_EXPRESS';
                const isDisabled = !selectedDepartment?.is_express_enabled && isExpress;

                return {
                  ...opt,
                  label: (
                    <span
                      style={{
                        color: isExpress && !isDisabled ? '#1890ff' : undefined,
                        opacity: isDisabled ? 0.5 : 1,
                      }}
                    >
                      {opt.label}
                    </span>
                  ),
                };
              })}
              onChange={onChange}
            />
            {hasChanges && (
              <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                <Button type={'link'} onClick={onSave}>
                  {savingDistricts[providersProps.districtId] ? 'Saving...' : 'Save'}
                </Button>
                <Button type={'link'} onClick={onCancel}>
                  Cancel
                </Button>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Drivers',
      dataIndex: 'data',
      render: (districtId: string) => (
        <DriverWithDistrictsViewNew drivers={secondTableRowStates[districtId]?.drivers || []} />
      ),
    },
    {
      title: 'Load Factor',
      dataIndex: 'loadFactor',
      render: (districtProps: { loadFactor: number; districtId: string }) => (
        <InputNumber
          min={1}
          max={7}
          defaultValue={districtProps.loadFactor}
          onChange={(e) => handleLoadFactorChange(e || 0, districtProps.districtId)}
        />
      ),
    },
    {
      title: (
        <span>
          <Tooltip title="A setting that shows how many hours to take away from the end of the working day so that the system can not show the schedule for today.">
            Cut off hours <QuestionCircleTwoTone />
          </Tooltip>
        </span>
      ),
      dataIndex: 'cutoffHours',
      render: (value: unknown, record: Record<string, unknown>) => {
        return (
          <InputNumber
            min={-10}
            max={0}
            defaultValue={(value as number) || 0}
            onChange={(e) => handleCutoffHoursChange(e || 0, record.data as string)}
          />
        );
      },
    },
    {
      title: (
        <span>
          Schedule limitation&nbsp;
          {!_limitedUsers.includes(userDataParams?.email || '') && (
            <Tooltip title="Only some people have access to this field">
              <QuestionCircleTwoTone />
            </Tooltip>
          )}
        </span>
      ),
      dataIndex: 'deliveryDays',
      render: (districtProps: { deliveryDays: number; districtId: string }) => (
        <InputNumber
          disabled={!_limitedUsers.includes(userDataParams?.email || '')}
          min={1}
          max={7}
          defaultValue={districtProps.deliveryDays}
          onChange={(e) => handleDeliveryDaysChange(e || 0, districtProps.districtId)}
        />
      ),
    },
    {
      title: 'Off days',
      dataIndex: 'offDays',
      render: (districtProps: { offDays: number[]; districtId: string }) => (
        <Select
          mode="multiple"
          allowClear
          style={{ width: '100%' }}
          placeholder="Select days"
          defaultValue={districtProps.offDays || []}
          options={_offDaysOptions}
          onChange={(offDays: number[]) => handleOffDaysChange(offDays, districtProps.districtId)}
        />
      ),
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      render: (districtProps: { enabled: boolean; districtId: string }) => (
        <Tag color={districtProps.enabled ? 'green' : 'red'}>
          {districtProps.enabled ? 'Enabled' : 'Disabled'}
        </Tag>
      ),
    },
  ];

  const districtsTableSkeleton = useTableSkeleton(columns, tableData, districtsLoading);

  return (
    <div>
      <div style={{ width: '100%', padding: '16px 16px 0' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            gap: 16,
            flexWrap: 'wrap',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Input.Search
            placeholder="Search districts by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 300, minWidth: 200, flexShrink: 1 }}
            allowClear
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexShrink: 0,
              flexWrap: 'wrap',
            }}
          >
            <Button
              type="primary"
              onClick={async () => {
                try {
                  const countryCode = selectedDepartment?.countryId;
                  if (!countryCode) {
                    message.error('Country is not selected or missing.');
                    return;
                  }
                  await updateCoverage(countryCode);
                  message.success('Coverage update started.');
                } catch (error) {
                  const errorMessage = formatError(error, 'Update Coverage');
                  logError(error, 'Update Coverage');
                  message.error(`Failed to update coverage: ${errorMessage}`);
                }
              }}
              loading={isUpdatingCoverage}
              disabled={!canUpdateCoverage}
            >
              Update coverage
            </Button>
            <Divider type="vertical" />
            <DepartmentExpressToggle />
          </div>
        </div>
      </div>
      <Table
        className="app-table"
        columns={districtsTableSkeleton.columns}
        dataSource={districtsTableSkeleton.dataSource}
        loading={districtsTableSkeleton.loading}
      />
    </div>
  );
};

export const _offDaysOptions = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 7 },
];

export const _limitedUsers = [
  //* Peru | Colombia
  'anton.lima@umbrella-trade.com',
  'vladimir.riazanov@umbrella-trade.com',
  'omar.velasquez@umbrella-trade.com',
  'lima.andrew.chicchon@umbrella-trade.com',
  'julio.paredes@umbrella-trade.com',
  'alex.arequipa@umbrella-trade.com',

  //* Chile
  'pavel.e@umbrella-trade.com',
  'natalia.chile@umbrella-trade.com',
  'christian.rojas@umbrella-trade.com',

  //* Developers
  'dydynovdima6@gmail.com',
];
